import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import { categories, featuredContents } from '@/mocks/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState(slug || 'movie');
  const activeCategory = categories.find((c) => c.slug === activeTab);
  const filteredContents = featuredContents.filter((c) => c.category === activeTab);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const catNames: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    anime: '动漫',
    variety: '综艺',
    documentary: '纪录片',
  };

  const seoTitle = `${activeCategory?.name || '分类'} - yy6080免费在线观看 | yy6080${activeCategory?.name || ''}`;
  const seoDesc = `yy6080${activeCategory?.name || ''}频道，海量${activeCategory?.name || ''}资源免费在线观看，高清画质流畅播放，每日更新热门${activeCategory?.name || ''}，尽在yy6080。`;

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaslab.github.io/' },
      { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类', item: `https://dramaslab.github.io/category/${activeTab}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`yy6080,yy6080在线观看,yy6080${activeCategory?.name || ''},yy6080最新${activeCategory?.name || ''}`}
        canonicalUrl={`https://dramaslab.github.io/category/${activeTab}`}
      />
      <StructuredData data={breadcrumbLD} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: catNames[activeTab] || activeCategory?.name || '分类' }]} />

          <div className="px-4 md:px-6 py-6 w-full">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  {activeCategory?.name || '分类'}频道
                </h1>
                <p className="text-sm text-foreground-600">{activeCategory?.description}</p>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => {
                      setActiveTab(cat.slug);
                      window.history.replaceState(null, '', `/category/${cat.slug}`);
                    }}
                    className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Content Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {filteredContents.map((item) => (
                  <CategoryContentCard key={item.id} item={item} />
                ))}
                {filteredContents.length === 0 && (
                  <div className="col-span-full text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                      <i className="ri-inbox-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">暂无相关内容，请选择其他分类</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function CategoryContentCard({ item }: { item: typeof featuredContents[number] }) {
  const categoryLabel = categories.find((c) => c.slug === item.category)?.name || '';

  return (
    <Link
      to={`/article/${item.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={item.image}
          alt={`${item.title} - yy6080在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={`${item.title} - yy6080${categoryLabel}免费播放`}
        />
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
          {item.rating}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
          <span className="text-white text-xs whitespace-nowrap">{item.episodeInfo}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-1 whitespace-nowrap">
          {item.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <span className="whitespace-nowrap">{categoryLabel}</span>
          <span className="whitespace-nowrap">{item.year}</span>
          <span className="whitespace-nowrap">{item.region}</span>
        </div>
      </div>
    </Link>
  );
}